import { ImageName } from "@mcleod/images";
import { CommonTooltips } from "@mcleod/common";
import { ClickEvent, Component, Image, Label, Panel, Table, TableRow, Textbox } from "@mcleod/components";
import { CompanySettings, ModelRow, Navigation, StringUtil } from "@mcleod/core";
import { setCommodityTooltipContent } from "@mcleod/powerbroker";
import { ManifestMovementQuickInfo } from "./ManifestMovementQuickInfo";
import { MultiStop } from "./MultiStop";
import { PartialShipmentPlanningTaskFilter } from "./PartialShipmentPlanningTaskFilter";
import { SecondaryFilterProps } from "./planning/FilterPanel";
import { PlanningUtil } from "./planning/PlanningUtil";

const SPLIT_PICKUP = "SP";
const SPLIT_DROP = "SD";

export class PartialShipmentPlanningUtil {

    public static getOrdersSecondaryFilters(): SecondaryFilterProps[] {
        const companySettings = CompanySettings.getSingleton();
        const companyTypeBoth = companySettings.isBoth();
        const companyTypeAsset = companySettings.isAsset();
        const companyTypeBrokerage = companySettings.isBrokerage();

        const filters: SecondaryFilterProps[] = [
            PartialShipmentPlanningTaskFilter.APPT_REQUIRED,
            PartialShipmentPlanningTaskFilter.APPT_CONFIRMED,
            PartialShipmentPlanningTaskFilter.ORDERS_TRUCKLOAD,
            PartialShipmentPlanningTaskFilter.Y_SPLIT_ORDERS_MOVEMENT,
        ];

        if (companyTypeBoth) {
            filters.push(PartialShipmentPlanningTaskFilter.ORDERS_ASSET);
            filters.push(PartialShipmentPlanningTaskFilter.ORDERS_BROKERAGE);
        } else if (companyTypeAsset) {
            filters.push(PartialShipmentPlanningTaskFilter.ORDERS_ASSET);
        } else if (companyTypeBrokerage) {
            filters.push(PartialShipmentPlanningTaskFilter.ORDERS_BROKERAGE);
        }

        return filters;
    }

    public static getManifestsSecondaryFilters(): SecondaryFilterProps[] {
        const companySettings = CompanySettings.getSingleton();
        const companyTypeBoth = companySettings.isBoth();
        const companyTypeAsset = companySettings.isAsset();
        const companyTypeBrokerage = companySettings.isBrokerage();

        const filters: SecondaryFilterProps[] = [
            PartialShipmentPlanningTaskFilter.MANIFESTS_SHELL,
            PartialShipmentPlanningTaskFilter.MANIFESTS_IN_PROGRESS,
            PartialShipmentPlanningTaskFilter.MANIFESTS_Y_SPLIT,
            PartialShipmentPlanningTaskFilter.MANIFESTS_ON_HOLD,
        ];

        if (companyTypeBoth) {
            filters.push(PartialShipmentPlanningTaskFilter.MANIFESTS_ASSET);
            filters.push(PartialShipmentPlanningTaskFilter.MANIFESTS_BROKERAGE);
        } else if (companyTypeAsset) {
            filters.push(PartialShipmentPlanningTaskFilter.MANIFESTS_ASSET);
        } else if (companyTypeBrokerage) {
            filters.push(PartialShipmentPlanningTaskFilter.MANIFESTS_BROKERAGE);
        }

        return filters;
    }

    public static formatWeight(tableRow: TableRow, modelRow: ModelRow) {
        const weightLabel = tableRow.findComponentById("labelWeight") as Label;
        const weight: number = modelRow.get("weight");
        if (weight) {
            weightLabel.text = weight < 1000 ? weight.toString() : weight.toLocaleString();
        }
    }

    public static formatMiles(tableRow: TableRow, modelRow: ModelRow) {
        const milesLabel = tableRow.findComponentById("labelMileage") as Label;
        const miles: number = modelRow.get("move_distance");
        if (miles) {
            milesLabel.text = miles < 1000 ? miles.toString() : miles.toLocaleString();
        }
    }

    public static setupOrderIdLabel(tableRow: TableRow, modelRow: ModelRow) {
        const orderIdLabel = tableRow.findComponentById("labelOrderId") as Label;
        orderIdLabel.addUnderlineWhenMouseOver();
        orderIdLabel.tooltip = "View Order " + modelRow.get("order_id") + " in new Tab";
    }

    public static setupManifestIdLabel(tableRow: TableRow) {
        const manifestIdLabel = tableRow.findComponentById("labelManifestId") as Label;
        manifestIdLabel.addUnderlineWhenMouseOver();
    }

    public static showBadgesInOrdersSection(tableRow: TableRow, modelRow: ModelRow) {
        const imageIcon = tableRow.findComponentById("imageBrokerageBadge") as Image;
        const brokerage: boolean = modelRow.get("brokerage", false);
        const originStopType = modelRow.get("origin_stop_type", "");
        const destStopType = modelRow.get("destination_stop_type", "");
        imageIcon.visible = brokerage;
        let ySplitImageIcon: Image;
        const imageId = this.getYsplitImageComponentByStopTypeId(originStopType, destStopType);
        if (imageId != null) {
            ySplitImageIcon = tableRow.findComponentById(imageId) as Image;
            ySplitImageIcon.visible = this.shouldDisplayYsplitIcon(originStopType, destStopType);
        }
    }

    public static showShellManifestIcon(modelRow: ModelRow, tableRow: TableRow) {
        const imageShellManifest = tableRow.findComponentById("imageShellManifest") as Image;
        const isShellManifest: boolean = modelRow.get("is_shell_manifests");
        imageShellManifest.visible = isShellManifest;
    }

    public static showBadgesInManifestsSection(tableRow: TableRow, modelRow: ModelRow) {
        const brokerage: boolean = modelRow.get("brokerage", false);
        const movementId: string = modelRow.get("movement_id");
        const manifestOnHold: boolean = modelRow.get("manifest_hold", false);
        const isManifestMovementProgress = modelRow.get("is_progress_manifests", false);
        const originStopType = modelRow.get("origin_stop_type", "");
        const destStopType = modelRow.get("destination_stop_type", "");
        const brokerageImageIcon = tableRow.findComponentById("imageBrokerageBadge") as Image;
        let ySplitImageIcon: Image;
        const holdImageIcon = tableRow.findComponentById("imageManifestHold") as Image;
        const inProgressImageIcon = tableRow.findComponentById("imageInProgress") as Image;
        const imageId = this.getYsplitImageComponentByStopTypeId(originStopType, destStopType);
        brokerageImageIcon.visible = brokerage;
        if (imageId != null) {
            ySplitImageIcon = tableRow.findComponentById(imageId) as Image;
            ySplitImageIcon.visible = this.shouldDisplayYsplitIcon(originStopType, destStopType)
        }
        holdImageIcon.visible = !StringUtil.isEmptyString(movementId) && manifestOnHold;
        inProgressImageIcon.visible = isManifestMovementProgress;
    }

    public static setPanelColors(panelContainer: Panel, panelStrip: Panel, label: Label, bgColor: string, textColor: string) {
        panelContainer.backgroundColor = bgColor;
        panelContainer.color = textColor;
        panelStrip.backgroundColor = textColor;
        label.color = textColor;
    }

    public static shouldDisplayYsplitIcon(originStopType: string, destStopType: string): boolean {
        return originStopType === SPLIT_PICKUP || destStopType === SPLIT_DROP;
    }

    public static getYsplitImageComponentByStopTypeId(originStopType: string, destStopType: string): string | null {
        if (originStopType === SPLIT_PICKUP && destStopType === SPLIT_DROP) {
            return "imageYsplitComplete";
        } else if (originStopType === SPLIT_PICKUP) {
            return "imageYsplitPick";
        } else if (destStopType === SPLIT_DROP) {
            return "imageYsplitDrop";
        } else {
            return null;
        }
    }

    public static setupApptRequiredConfirmed(tableRow: TableRow, modelRow: ModelRow, componentId: string, isOrigin: string) {
        const apptBadgeComp = tableRow.findComponentById(componentId) as Image;
        let requiredFieldName: string;
        let confirmedFieldName: string;
        if (isOrigin === "origin") {
            requiredFieldName = "origin_appt_required"
            confirmedFieldName = "origin_appt_confirmed"
        } else {
            requiredFieldName = "destination_appt_required"
            confirmedFieldName = "destination_appt_confirmed"
        }
        if (apptBadgeComp) {
            if (modelRow.get(requiredFieldName) && modelRow.get(confirmedFieldName)) {
                apptBadgeComp.visible = true;
                apptBadgeComp.name = ImageName.appointmentConfirmed;
                apptBadgeComp.tooltip = "Appointment Confirmed";
                apptBadgeComp.color = "success";
                apptBadgeComp.marginLeft = "5"
            }
            else if (modelRow.get(confirmedFieldName)) {
                apptBadgeComp.visible = true;
                apptBadgeComp.name = ImageName.appointmentConfirmed;
                apptBadgeComp.tooltip = "Appointment Confirmed";
                apptBadgeComp.color = "success";
                apptBadgeComp.marginLeft = "5"
            }
            else if (modelRow.get(requiredFieldName)) {
                apptBadgeComp.visible = true;
                apptBadgeComp.color = "caution";
                apptBadgeComp.tooltip = "Appointment Required";
                apptBadgeComp.marginLeft = "5"
            }
         }
    }

    public static setTooltipContent(comp: Component, modelRow: ModelRow<any>) {
        if (comp.id == null) return;
        const movementId = modelRow.get("movement_id");
        if ("labelCommodity" === comp.id) {
            setCommodityTooltipContent(comp, modelRow);
            return;
        }
        if ("panelOriginCityStateZip" === comp.id && !StringUtil.isEmptyString(modelRow.get("origin_stop_id"))) {
            const originStopId = modelRow.get("origin_stop_id");
            return CommonTooltips.setTooltipContentLayout(comp, originStopId, "lme/dispatch/planning/PlanningLocationQuickInfo", {
                minWidth: 128,
                minHeight: 140
            });
        }
        if ("panelDestinationCityStateZip" === comp.id && !StringUtil.isEmptyString(modelRow.get("destination_stop_id"))) {
            const destinationStopId = modelRow.get("destination_stop_id");
            return CommonTooltips.setTooltipContentLayout(comp, destinationStopId, "lme/dispatch/planning/PlanningLocationQuickInfo", {
                minWidth: 128,
                minHeight: 140
            });
        }

        if (("panelOrdersPickDrops" === comp.id || "panelManifestsPickDrops"  === comp.id) && !StringUtil.isEmptyString(movementId)) {
            const props = { maxWidth: 800, maxHeight: 600 };
            CommonTooltips.setTooltipContentLayout(comp, null, "lme/dispatch/StopAppointmentStatusList", props, { movement_id: movementId });
        }

        if ("labelManifestId" === comp.id) {
            const manifestId = modelRow.get("manifest_id");
            if (!StringUtil.isEmptyString(movementId)) {
                ManifestMovementQuickInfo.addHoverToDiscoverToManifestIdComponent(comp as Label, movementId, manifestId);
            } else {
                const description = modelRow.get("manifest_id_description");
                comp.tooltip = description || "Shell Manifest: " + manifestId;
            }
        }
    }

    public static async showMultiStopSlideout(event) {
        const label = event.target as Label;
        label.busy = true;
        const tableRow: TableRow = TableRow.getContainingTableRow(event.target);
        tableRow.table.selectedRow = tableRow;
        const row = tableRow.data;
        const isManifest = row.get("manifest_id") != null;
        const displayed = await MultiStop.showSlideout({isManifest, row, doAfterSlideOut: () => label.busy = false});
        if (displayed === false) {
            label.busy = false;
        }
    }

    public static manifestCommodities(tableRow: TableRow, modelRow: ModelRow) {
        const textboxCommodities = tableRow.findComponentById("textboxCommdities") as Textbox;
        const manifestCommodities: string = modelRow.get("manifest_commodities");
        if (manifestCommodities && manifestCommodities.includes(",")) {
            textboxCommodities.color = "primary";
        }
    }

    public static showHideComponent(tableRow: TableRow, labelId: string, dataKey: string) {
        const component = tableRow.findComponentById(labelId);
        if (component) {
            if (component instanceof Label || component instanceof Panel || component instanceof Textbox) {
                const keyValue = tableRow.data.get(dataKey);
                component.visible = !!keyValue;
            }
        }
    }

    public static showHideInterimStops(tableRow: TableRow, modelRow: ModelRow) {
        const interimStopRequiredImage = tableRow.findComponentById("imageInterirmRequiredStop") as Image;
        const interimStopConfirmedImage = tableRow.findComponentById("imageInterirmConfirmedStop") as Image;
        const labelStopRequiredImage = tableRow.findComponentById("labelInterimRequiredCount") as Label;
        const labelStopConfirmedImage = tableRow.findComponentById("labelInterimConfirmedCount") as Label;
        const requiredCount: number = modelRow.get("interim_stops_required_count", 0);
        const confirmedCount: number = modelRow.get("interim_stops_confirmed_count", 0);
        interimStopRequiredImage.visible = requiredCount > 0;
        labelStopRequiredImage.visible = requiredCount > 0;
        interimStopConfirmedImage.visible = confirmedCount > 0;
        labelStopConfirmedImage.visible = confirmedCount > 0;
    }

    public static showMutilStopSliderFromOrders(tableRow: TableRow) {
        const imageOriginBadge = tableRow.findComponentById("imageOriginApptRequireConfirmed") as Image;
        const imageDestinationBadge = tableRow.findComponentById("imageDestinationApptRequireConfirmed") as Image;
        const panelInterimStopsCounter = tableRow.findComponentById("panelStopsInterimCounter") as Panel;
        const panelPickDropCounter = tableRow.findComponentById("panelOrdersPickDrops") as Panel;

        const addClickListener = (badge: Image | Panel) => {
            badge.addClickListener((event: ClickEvent) => {
                PlanningUtil.showMultiStopSlideout(event.target as Label, true);
            });
        };

        addClickListener(imageOriginBadge);
        addClickListener(imageDestinationBadge);
        addClickListener(panelInterimStopsCounter);
        addClickListener(panelPickDropCounter);
    }

    public static showOrderHoldBadgeAndReason(tableRow: TableRow, modelRow: ModelRow) {
        const imageOrderHold = tableRow.findComponentById("imageOrderOnHold") as Image;
        if (imageOrderHold) {
            const orderHold: boolean = modelRow.get("order_hold", false);
            imageOrderHold.visible = orderHold;
            if (orderHold) {
                const holdReason: string = modelRow.get("order_hold_reason");
                imageOrderHold.tooltip = !StringUtil.isEmptyString(orderHold) ? holdReason : "Order on Hold";
            }
        }
    }
    public static showMutilStopSliderFromManifests(tableRow: TableRow) {
        const imageOriginBadge = tableRow.findComponentById("imageOriginApptRequireConfirmed") as Image;
        const imageDestinationBadge = tableRow.findComponentById("imageDestinationApptRequireConfirmed") as Image;
        const panelPickDropCounter = tableRow.findComponentById("panelManifestsPickDrops") as Panel;
        const panelInterimStopsCounter = tableRow.findComponentById("panelStopsInterimCounter") as Panel;

        const addClickListener = (badge: Image | Panel) => {
            badge.addClickListener((event: ClickEvent) => {
                this.showMultiStopSlideout(event);
            });
        };

        addClickListener(imageOriginBadge);
        addClickListener(imageDestinationBadge);
        addClickListener(panelPickDropCounter);
        addClickListener(panelInterimStopsCounter);
    }

    public static addContextMenuItemOrdersTable(table: Table, tableRow: TableRow, modelRow: ModelRow): Partial<import("@mcleod/components").ContextMenuLabelProps>[] {
        if (table.selectedRow !== tableRow) {
            table.selectedRow = tableRow;
        }
        const url = "lme/dispatch/OrderTabView?mode=update&key="+ modelRow.get("order_id");
        const menuItems: Partial<import("@mcleod/components").ContextMenuLabelProps>[] = [];
        menuItems.push({text: "Open Order in New Tab", onClick: () => Navigation.navigateTo(url, { newTab: true })});
        menuItems.push({text: "Open Order in New Window", onClick: () => Navigation.navigateTo(url, { newTab: true, windowDecorators: false })});
        return menuItems;
    }

}
